import React, { useEffect, useMemo, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    MenuItem,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { getFeatureFlagHistory, searchScopeOptions, updateFeatureFlag } from './featureFlags.service';

const SCOPE_TYPES = [
    { value: 'ECOSYSTEM', label: 'Ecosystem', hint: 'Everyone in it: integrator, members, branches and staff' },
    { value: 'PERSONA', label: 'Persona', hint: 'Every user of this persona' },
    { value: 'USER', label: 'User', hint: 'One person, found by name or phone' },
];

/** Readable labels for the settings the backend defines per flag; unknown ones show their key. */
const SETTING_LABELS = {
    liveSmsForPin:
        'Codes that set or reset a PIN are always sent by real SMS for these users (switch off on QA, whose test phones are fake)',
};

const scopeTypeLabel = (type) => SCOPE_TYPES.find((t) => t.value === type)?.label ?? type;

const formatDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
};

/** A snapshot from the change log in one line: "On · 10% · 2 scopes · liveSmsForPin: on". */
const summarise = (snapshot) => {
    try {
        const s = JSON.parse(snapshot);
        const settings = Object.entries(s.settings || {}).map(([k, v]) => `${k}: ${v ? 'on' : 'off'}`);
        return [s.enabled ? 'On' : 'Off', `${s.rolloutPercent}%`, `${(s.scopes || []).length} scope(s)`, ...settings].join(' · ');
    } catch {
        return snapshot;
    }
};

const toDraft = (flag) => ({
    enabled: flag.enabled,
    rolloutPercent: flag.rolloutPercent,
    settings: { ...(flag.settings || {}) },
    scopes: (flag.scopes || []).map((s) => ({ type: s.type, id: s.id, label: s.label })),
});

/**
 * One feature flag: on/off, who it covers (ecosystems, personas, users), a rollout percentage of all
 * users on top of that, its settings, and its change log. Saving replaces the whole configuration.
 */
function FeatureFlagCard({ flag, onSaved, onNotify }) {
    const [draft, setDraft] = useState(() => toDraft(flag));
    const [scopeType, setScopeType] = useState('ECOSYSTEM');
    const [search, setSearch] = useState('');
    const [options, setOptions] = useState([]);
    const [optionsLoading, setOptionsLoading] = useState(false);
    const [picked, setPicked] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [history, setHistory] = useState(null);

    useEffect(() => setDraft(toDraft(flag)), [flag]);

    // Options for the picker: personas all at once, ecosystems and users by search (debounced).
    useEffect(() => {
        let active = true;
        const timer = setTimeout(async () => {
            if (scopeType !== 'PERSONA' && search.trim().length < 2) {
                setOptions([]);
                return;
            }
            setOptionsLoading(true);
            try {
                const found = await searchScopeOptions(scopeType, scopeType === 'PERSONA' ? '' : search);
                if (active) setOptions(found);
            } catch (error) {
                if (active) onNotify(error.message, 'error');
            } finally {
                if (active) setOptionsLoading(false);
            }
        }, 300);
        return () => {
            active = false;
            clearTimeout(timer);
        };
    }, [scopeType, search, onNotify]);

    const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(toDraft(flag)), [draft, flag]);
    const percentValid = Number.isInteger(draft.rolloutPercent) && draft.rolloutPercent >= 0 && draft.rolloutPercent <= 100;

    const addScope = () => {
        if (!picked) return;
        const exists = draft.scopes.some((s) => s.type === scopeType && s.id === picked.id);
        if (!exists) {
            setDraft((d) => ({ ...d, scopes: [...d.scopes, { type: scopeType, id: picked.id, label: picked.label }] }));
        }
        setPicked(null);
        setSearch('');
    };

    const removeScope = (scope) =>
        setDraft((d) => ({ ...d, scopes: d.scopes.filter((s) => !(s.type === scope.type && s.id === scope.id)) }));

    const save = async () => {
        setSaving(true);
        try {
            const saved = await updateFeatureFlag(flag.key, {
                enabled: draft.enabled,
                rolloutPercent: draft.rolloutPercent,
                settings: draft.settings,
                scopes: draft.scopes.map(({ type, id }) => ({ type, id })),
            });
            onSaved(saved);
            onNotify(`${flag.key} saved`, 'success');
            if (history) setHistory(await getFeatureFlagHistory(flag.key));
        } catch (error) {
            onNotify(error.message, 'error');
        } finally {
            setSaving(false);
            setConfirmOpen(false);
        }
    };

    const toggleHistory = async () => {
        if (history) {
            setHistory(null);
            return;
        }
        try {
            setHistory(await getFeatureFlagHistory(flag.key));
        } catch (error) {
            onNotify(error.message, 'error');
        }
    };

    const reach = draft.enabled
        ? `${draft.scopes.length} scope entr${draft.scopes.length === 1 ? 'y' : 'ies'}${draft.rolloutPercent > 0 ? ` and ${draft.rolloutPercent}% of all users` : ''}`
        : 'nobody (switched off)';

    return (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ justifyContent: 'space-between', alignItems: { sm: 'flex-start' } }}>
                <Box>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <Typography sx={{ fontFamily: 'monospace', fontSize: 18, fontWeight: 700 }}>{flag.key}</Typography>
                        <Chip size="small" label={flag.enabled ? 'On' : 'Off'} color={flag.enabled ? 'success' : 'default'} />
                    </Stack>
                    <Typography color="text.secondary" sx={{ mt: 0.5 }}>{flag.description}</Typography>
                    {flag.updatedAt ? (
                        <Typography variant="caption" color="text.secondary">
                            Last changed {formatDate(flag.updatedAt)}{flag.updatedByName ? ` by ${flag.updatedByName}` : ''}
                        </Typography>
                    ) : null}
                </Box>
                <FormControlLabel
                    control={
                        <Switch
                            checked={draft.enabled}
                            onChange={(e) => setDraft((d) => ({ ...d, enabled: e.target.checked }))}
                            inputProps={{ 'aria-label': `Enable ${flag.key}` }}
                        />
                    }
                    label="Enabled"
                />
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography sx={{ fontWeight: 600, mb: 1 }}>Scope</Typography>
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {draft.scopes.length === 0 ? (
                    <Typography color="text.secondary">No one yet. Add an ecosystem, persona or user below.</Typography>
                ) : (
                    draft.scopes.map((scope) => (
                        <Chip
                            key={`${scope.type}|${scope.id}`}
                            label={`${scopeTypeLabel(scope.type)}: ${scope.label || scope.id}`}
                            onDelete={() => removeScope(scope)}
                        />
                    ))
                )}
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ alignItems: { md: 'center' } }}>
                <TextField
                    select
                    size="small"
                    label="Add"
                    value={scopeType}
                    onChange={(e) => {
                        setScopeType(e.target.value);
                        setPicked(null);
                        setSearch('');
                    }}
                    sx={{ minWidth: 160 }}
                >
                    {SCOPE_TYPES.map((t) => (
                        <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                    ))}
                </TextField>
                <Autocomplete
                    size="small"
                    sx={{ minWidth: { md: 360 }, flex: 1 }}
                    options={options}
                    loading={optionsLoading}
                    value={picked}
                    inputValue={search}
                    filterOptions={(x) => (scopeType === 'PERSONA'
                        ? x.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
                        : x)}
                    getOptionLabel={(o) => o.label || o.id}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    onChange={(_e, value) => setPicked(value)}
                    onInputChange={(_e, value) => setSearch(value)}
                    noOptionsText={scopeType === 'PERSONA' || search.trim().length >= 2 ? 'No match' : 'Type at least 2 characters'}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={SCOPE_TYPES.find((t) => t.value === scopeType)?.hint}
                            slotProps={{
                                input: {
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {optionsLoading ? <CircularProgress size={16} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                },
                            }}
                        />
                    )}
                />
                <Button variant="outlined" onClick={addScope} disabled={!picked}>Add to scope</Button>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                <Box sx={{ minWidth: 220 }}>
                    <Typography sx={{ fontWeight: 600, mb: 1 }}>Rollout</Typography>
                    <TextField
                        size="small"
                        type="number"
                        label="% of all users"
                        value={Number.isNaN(draft.rolloutPercent) ? '' : draft.rolloutPercent}
                        onChange={(e) => setDraft((d) => ({ ...d, rolloutPercent: e.target.value === '' ? NaN : Number(e.target.value) }))}
                        error={!percentValid}
                        helperText={percentValid ? 'On top of the scope. 0 = scope only.' : 'A whole number from 0 to 100'}
                        slotProps={{ htmlInput: { min: 0, max: 100, step: 1 } }}
                    />
                </Box>
                {Object.keys(draft.settings).length > 0 ? (
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontWeight: 600, mb: 1 }}>Settings</Typography>
                        {Object.entries(draft.settings).map(([name, value]) => (
                            <FormControlLabel
                                key={name}
                                control={
                                    <Checkbox
                                        checked={Boolean(value)}
                                        onChange={(e) => setDraft((d) => ({ ...d, settings: { ...d.settings, [name]: e.target.checked } }))}
                                    />
                                }
                                label={SETTING_LABELS[name] || name}
                            />
                        ))}
                    </Box>
                ) : null}
            </Stack>

            <Stack direction="row" spacing={1.5} sx={{ mt: 3, justifyContent: 'flex-end' }}>
                <Button onClick={toggleHistory}>{history ? 'Hide change log' : 'Change log'}</Button>
                <Button disabled={!dirty || saving} onClick={() => setDraft(toDraft(flag))}>Discard</Button>
                <Button variant="contained" disabled={!dirty || !percentValid || saving} onClick={() => setConfirmOpen(true)}>
                    Save
                </Button>
            </Stack>

            {history ? (
                <Box sx={{ mt: 2 }}>
                    {history.length === 0 ? (
                        <Typography color="text.secondary">No changes yet.</Typography>
                    ) : (
                        history.map((event) => (
                            <Typography key={`${event.changedAt}-${event.snapshot}`} variant="body2" sx={{ py: 0.5 }}>
                                <strong>{formatDate(event.changedAt)}</strong>
                                {event.changedByName ? ` · ${event.changedByName}` : ''} · {summarise(event.snapshot)}
                            </Typography>
                        ))
                    )}
                </Box>
            ) : null}

            <Dialog open={confirmOpen} onClose={() => (saving ? undefined : setConfirmOpen(false))} fullWidth maxWidth="xs">
                <DialogTitle>Save {flag.key}?</DialogTitle>
                <DialogContent>
                    <Typography>It will apply to {reach}, from their next sign-in.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button disabled={saving} onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    <Button variant="contained" disabled={saving} onClick={save}>Save</Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
}

export default FeatureFlagCard;
